import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';

import {
    TBaseOption,
    TPeriod,
    TPurchaseOptionsProps,
} from '@r1-frontend/ui-react/components/costs/PurchaseOptions/types';
import SlideCarousel from '@r1-frontend/ui-react/components/SlideCarousel';
import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';

import PurchaseOptionsItem
    from '~/src/features/full-buy/components/PurchaseOptionsSlider/components/PurchaseOptionsItem';
import { ILimits } from '~/src/features/full-buy/helpersForProducts';

import * as ST from './styled';

type TPurchaseOptionsSliderProps = {
    options: TPurchaseOptionsProps['options'],
    selectedOption: { cost: number, period: TPeriod },
    handleSelect: (cost: number, period: TPeriod) => () => void,
    hasAdded: boolean,
    limits: ILimits,
}

const PurchaseOptionsSlider = ({
    options,
    selectedOption,
    handleSelect,
    hasAdded,
    limits,
}: TPurchaseOptionsSliderProps) => {
    const {
        1: fullPriceOption,
        ...elseOptions
    } = options;

    const isChecked = (option: TBaseOption) => {
        return option.cost === selectedOption?.cost && option.period === selectedOption.period;
    };

    const { sortedOptions, lowestPriceOption } = useMemo(() => {
        const sortedOptions = sortBy(Object.values(elseOptions), 'cost');
        return { sortedOptions, lowestPriceOption: sortedOptions[0] };
    }, [elseOptions]);

    const isFullPriceChecked = isChecked(fullPriceOption);
    const isFullPriceDisabled = hasAdded && !isFullPriceChecked || limits.total;

    const isLowestPriceChecked = lowestPriceOption && isChecked(lowestPriceOption) || !isFullPriceChecked;
    const isLowestPriceDisabled = lowestPriceOption && hasAdded && isFullPriceChecked || limits.installment;

    return (
        <ST.PurchaseContainer>
            <H4 $smaller>Варианты приобретения</H4>
            <SlideCarousel
                withoutArrows={true}
                withoutDots={true}
                slideWidth={170}
                cellSpacing={8}
                framePadding={'0 0 24px 0px'}
            >
                <PurchaseOptionsItem
                    onSelect={handleSelect}
                    customText={'Полная стоимость'}
                    isChecked={isFullPriceChecked}
                    {...fullPriceOption}
                    disabled={isFullPriceDisabled}
                />
                {lowestPriceOption && <PurchaseOptionsItem
                    onSelect={handleSelect}
                    customText={`Рассрочка\nот ${lowestPriceOption.cost} ₽/мес`}
                    isChecked={isLowestPriceChecked}
                    {...lowestPriceOption}
                    disabled={isLowestPriceDisabled}
                />}
            </SlideCarousel>
            {
                selectedOption && selectedOption?.period > 0 && (
                    <>
                        <Paragraph4 $withIndent $smaller>Выберите длительность рассрочки:</Paragraph4>
                        <SlideCarousel
                            withoutArrows={true}
                            withoutDots={true}
                            slideWidth={206}
                            cellSpacing={8}
                            framePadding={'0 0 24px 0px'}
                        >
                            {sortedOptions.map((option) => {
                                return (
                                    <PurchaseOptionsItem
                                        key={option.period}
                                        onSelect={handleSelect}
                                        isChecked={isChecked(option)}
                                        name={'installment'}
                                        {...option}
                                        disabled={hasAdded && !isChecked(option)}
                                    />
                                );
                            })}
                        </SlideCarousel>
                    </>
                )
            }
        </ST.PurchaseContainer>
    );
};

export default PurchaseOptionsSlider;
